import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { AssemblyChildNodeSummary } from '@/services/assembly-branch-node-list';

/**
 * 装配体分支子节点列表上下文。
 */
export interface AssemblyBranchNodeListContextInput {
  /** 当前装配体节点 ID。 */
  readonly assemblyNodeId: string;
  /** 当前装配体分支 ID。 */
  readonly branchId: string;
  /** 当前装配体提交 ID；用于按历史提交读取直接子节点。 */
  readonly commitId?: string;
  /** 当前装配体节点显示名称。 */
  readonly assemblyNodeName: string;
}

/**
 * 复制装配体子节点摘要。
 *
 * 设计说明：服务层和组件层可能复用同一个节点对象引用。Store 写入时复制节点和 branches，
 * 可以保证后续外部对象被修改时不会绕过 Pinia action 污染 UI 状态。
 *
 * @param node 外部传入的子节点摘要。
 * @returns 可由 Store 独立持有的子节点摘要副本。
 */
function cloneNode(node: AssemblyChildNodeSummary): AssemblyChildNodeSummary {
  return {
    ...node,
    branches: node.branches.map((branch) => ({ ...branch })),
  };
}

/**
 * 装配体分支子节点列表 Store。
 *
 * 职责边界：
 * 1. 只维护左侧分支面板所需的 UI 状态；
 * 2. 不请求接口，不操作 Viewer，不参与 Undo/Redo；
 * 3. Controller 负责把服务结果写入 Store，组件只读取状态并派发事件。
 */
export const useAssemblyBranchNodeListStore = defineStore('assembly-branch-node-list', () => {
  /** 当前装配体节点 ID。 */
  const currentAssemblyNodeId = ref<string | null>(null);
  /** 当前装配体分支 ID。 */
  const currentBranchId = ref<string | null>(null);
  /** 当前装配体提交 ID。 */
  const currentCommitId = ref<string | null>(null);
  /** 当前装配体节点显示名称。 */
  const currentAssemblyNodeName = ref('');
  /** 当前装配体直接子节点列表。 */
  const nodes = ref<AssemblyChildNodeSummary[]>([]);
  /** 子节点列表加载状态。 */
  const isLoading = ref(false);
  /** 切换采纳版本动作状态。 */
  const isSwitchingVersion = ref(false);

  /** 当前子节点数量显示文案。 */
  const nodeCountText = computed(() => `${nodes.value.length} 个子节点`);

  /**
   * 写入当前分支面板上下文。
   *
   * @param input 当前装配体节点与分支上下文。
   */
  function setContext(input: AssemblyBranchNodeListContextInput): void {
    currentAssemblyNodeId.value = input.assemblyNodeId;
    currentBranchId.value = input.branchId;
    currentCommitId.value = input.commitId?.trim() || null;
    currentAssemblyNodeName.value = input.assemblyNodeName;
  }

  /**
   * 替换子节点列表。
   *
   * 设计说明：这里复制整棵浅层摘要数据，避免服务 mock、Controller 或测试对象在写入后继续修改，
   * 导致面板状态出现不可追踪的隐式变化。
   *
   * @param nextNodes 最新子节点列表。
   */
  function setNodes(nextNodes: readonly AssemblyChildNodeSummary[]): void {
    nodes.value = nextNodes.map(cloneNode);
  }

  /**
   * 插入或更新单个子节点。
   *
   * 设计说明：采纳版本切换会返回单个节点摘要；统一在这里收口，
   * 可以保持列表替换策略一致，并继续隔离外部对象引用。
   *
   * @param nextNode 最新子节点摘要。
   */
  function upsertNode(nextNode: AssemblyChildNodeSummary): void {
    const clonedNode = cloneNode(nextNode);
    const existingIndex = nodes.value.findIndex((node) => node.nodeId === nextNode.nodeId);

    if (existingIndex >= 0) {
      nodes.value.splice(existingIndex, 1, clonedNode);
      return;
    }

    nodes.value.unshift(clonedNode);
  }

  /**
   * 重置分支子节点列表面板状态。
   */
  function reset(): void {
    currentAssemblyNodeId.value = null;
    currentBranchId.value = null;
    currentCommitId.value = null;
    currentAssemblyNodeName.value = '';
    nodes.value = [];
    isLoading.value = false;
    isSwitchingVersion.value = false;
  }

  return {
    currentAssemblyNodeId,
    currentBranchId,
    currentCommitId,
    currentAssemblyNodeName,
    nodes,
    isLoading,
    isSwitchingVersion,
    nodeCountText,
    setContext,
    setNodes,
    upsertNode,
    reset,
  };
});
